import { Icon } from "@platform/components";
import cx from "classix";
import { useEffect, useState } from "react";

const GENERATION_STEPS = [
  { label: 'Analyzing your profile', icon: 'book-open-01' },
  { label: 'Understanding your challenge', icon: 'target-02' },
  { label: 'Scoping deliverables', icon: 'users-01' },
  { label: 'Drafting your project', icon: 'file-pen' },
] as const;

const STEP_DURATION_MS = 3000;

const GeneratingSuggestionScreen = ({
  challengeNumber,
  organizationId,
  setLoading,
}: {
  challengeNumber: number;
  organizationId: number;
  setLoading: (v: boolean) => void;
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [animationDone, setAnimationDone] = useState(false);

  useEffect(() => {
    const timers = GENERATION_STEPS.map((_, i) =>
      setTimeout(() => setCurrentStep(i + 1), STEP_DURATION_MS * (i + 1)),
    );
    const animTimer = setTimeout(
      () => setAnimationDone(true),
      STEP_DURATION_MS * GENERATION_STEPS.length,
    );
    return () => [...timers, animTimer].forEach(clearTimeout);
  }, [organizationId, challengeNumber]);

  useEffect(() => {
    if (animationDone) {
      setLoading(false);
    }
  }, [animationDone, setLoading]);

  return (
    <div className='mt-8 mb-16 text-gray-700'>
      <div className='relative overflow-hidden border border-gray-200 rounded-2xl p-8 bg-white max-w-212 flex flex-col items-center gap-6 shadow-xs'>
        <div className='absolute top-0 left-0 right-0 h-0.75'>
          <div className='absolute inset-0 bg-linear-to-r from-transparent via-brand-500 to-transparent animate-shimmer' />
        </div>
        <div className='text-center'>
          <h2 className='text-2xl font-bold'>Generating your project…</h2>
          <p className='text-gray-500 mt-1'>This usually takes about a minute.</p>
        </div>
        <div className='relative flex flex-col items-center justify-center'>
          <div className='absolute w-15 h-15 rounded-full border-2 border-brand-400 animate-ping-persist' style={{ animationDuration: '3s' }} />
          <div className='z-10 w-20 h-20 rounded-full bg-gradient-to-r from-[#F5FAFF] to-[#C5DBFF] flex items-center justify-center'>
            <Icon type={GENERATION_STEPS[Math.min(currentStep, GENERATION_STEPS.length - 1)].icon} size='40' stroke='brand-500' />
          </div>
        </div>
        <div>
          <h3 className='font-semibold mt-3'>
            {GENERATION_STEPS[Math.min(currentStep, GENERATION_STEPS.length - 1)].label}
            <span className='inline-flex gap-0.5 ml-0.5'>
              <span className='animate-dot-pulse' style={{ animationDelay: '0ms' }}>.</span>
              <span className='animate-dot-pulse' style={{ animationDelay: '160ms' }}>.</span>
              <span className='animate-dot-pulse' style={{ animationDelay: '320ms' }}>.</span>
            </span>
          </h3>
        </div>
        <div className='h-px w-full bg-gray-100' />
        <div className='flex justify-between mt-4 flex-col lg:flex-row lg:items-center lg:w-full'>
          {GENERATION_STEPS.map((step, i) => {
            const isCompleted = i < currentStep;
            const isInProgress = i === currentStep;
            return (
              <div key={step.label} className='flex items-center gap-2 mb-2'>
                {isCompleted ? (
                  <div className='w-6 h-6 rounded-full bg-success-100 flex items-center justify-center shrink-0'>
                    <Icon type='check' size='14' stroke='success-500' />
                  </div>
                ) : isInProgress ? (
                  <div className='w-6 h-6 rounded-full border-2 border-brand-500 bg-secondary-brand-30 flex items-center justify-center shrink-0'>
                    <div className='bg-brand-500 rounded-full w-1 h-1 animate-ping-persist' style={{ animationDuration: '1.4s' }} />
                  </div>
                ) : (
                  <div className='w-6 h-6 rounded-full bg-gray-200 shrink-0 flex items-center justify-center'>
                    <div className='bg-gray-300 rounded-full w-1 h-1' />
                  </div>
                )}
                <span
                  className={cx(
                    'text-xs font-medium',
                    isCompleted && '',
                    isInProgress && 'font-semibold',
                    !isCompleted && !isInProgress && 'text-gray-300',
                  )}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GeneratingSuggestionScreen;